/**
 * HTTPS Utilities for Mixed Content Prevention
 * Ensures all API calls use HTTPS in production environments
 */

/**
 * Force HTTPS for production URLs to prevent Mixed Content errors
 * @param {string} url - The URL to check and potentially convert to HTTPS
 * @returns {string} - The URL with HTTPS if needed
 */
export function forceHttps(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  // List of production domains that should use HTTPS
  const productionDomains = [
    'awsapprunner.com',
    'app.medifusionpro.io',
    'api.medifusionpro.io',
    'rmtnev3xgz.ap-southeast-2.awsapprunner.com',
    'medifusionpro.io'
  ];

  // Check if URL starts with http:// and contains any production domain
  if (url.startsWith('http://')) {
    const isProduction = productionDomains.some(domain => url.includes(domain));
    
    if (isProduction) {
      const httpsUrl = url.replace('http://', 'https://');
      console.log('🔒 Forced HTTPS for production URL:', httpsUrl);
      return httpsUrl;
    }
  }

  return url;
}

/**
 * Get the base API URL with HTTPS enforcement
 * @returns {string} - The base API URL with HTTPS if needed
 */
export function getApiBaseUrl() {
  let baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  return forceHttps(baseUrl);
}

/**
 * Check if we're in a production environment
 * @returns {boolean} - True if in production
 */
export function isProduction() {
  return process.env.NODE_ENV === 'production' || 
         window.location.hostname.includes('medifusionpro.io') ||
         window.location.hostname.includes('awsapprunner.com');
}

/**
 * Get the appropriate API URL based on environment
 * @returns {string} - The correct API URL for the current environment
 */
export function getEnvironmentApiUrl() {
  console.log('🔍 Environment check:', {
    NODE_ENV: process.env.NODE_ENV,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    REACT_APP_API_URL: process.env.REACT_APP_API_URL
  });
  
  if (isProduction()) {
    // In production, always use HTTPS
    const productionUrl = 'https://rmtnev3xgz.ap-southeast-2.awsapprunner.com/api/v1';
    const envUrl = process.env.REACT_APP_API_URL;
    
    if (envUrl) {
      const httpsUrl = forceHttps(envUrl);
      console.log('🔒 Production URL conversion:', envUrl, '→', httpsUrl);
      return httpsUrl;
    } else {
      console.log('🔒 Using default production URL:', productionUrl);
      return productionUrl;
    }
  } else {
    // In development, use the configured URL
    const devUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    console.log('🔧 Development URL:', devUrl);
    return devUrl;
  }
}
